import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { PutJson, PostJson, GetJson } from '@awp/helpers/fetchReact';
import { REST_API, FORM_MODE } from '@awp/constants';
import { FormInput, FormSwitch, SubAreaCodeInput } from '@awp/components/Common/AwpFormInputs';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { Permissions } from '@awp/constants';
import Authorize from '@awp/components/Authorize';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import { Crud } from '@awp/shared/Crud';

const MODE = {
  SUB_EDIT: 'subedit',
  SUB_VIEW: 'subview',
  SUB_CREATE: 'subcreate',
};

const AvalancheSubAreasEdit = ({ setMode, mode, subData, subAreas, setSubAreas, avalancheAreaData }) => {
  const [avalancheSubAreaData, setAvalancheSubAreaData] = useState(mode !== MODE.SUB_CREATE ? subData : {});
  const [entityErrors, setEntityErrors] = useState(null);

  useEffect(() => {
    if (mode !== MODE.SUB_CREATE) {
      loadAvalancheSubArea();
    }
  }, []);

  const readOnly = mode !== MODE.SUB_EDIT && mode !== MODE.SUB_CREATE;
  const title =
    mode === MODE.SUB_EDIT
      ? 'Update Avalanche Sub Area'
      : mode === MODE.SUB_CREATE
      ? 'Create Avalanche Sub Area'
      : 'Avalanche Sub Area';

  const crud = new Crud(title);

  const loadAvalancheSubArea = async () => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicavalanchesubarea/${avalancheSubAreaData.value}`;

    GetJson(url)
      .then((response) => response.json())
      .then(async (record) => {
        if (record) {
          setAvalancheSubAreaData((prev) => ({
            ...prev,
            isDisabled: record.IsActive === 'N',
            ConcurrencyControlNumber: record.ConcurrencyControlNumber,
          }));
        }
      });
  };

  const createAvalancheSubAreas = (setSubmitting, resetForm, setErrors, avalancheSubAreaDto) => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}avalanchesubarea`;
    PostJson(url, avalancheSubAreaDto)
      .then(async (response) => {
        if (response.ok) {
          const avalancheSubArea = await response.json();
          updateData({
            value: avalancheSubArea.Id,
            name: avalancheSubArea.Name,
            code: avalancheSubArea.Code,
            isDisabled: !avalancheSubArea.IsActive,
            parentId: avalancheSubArea.AreaId,
          });
          resetForm({
            values: {
              validateOnMount: true,
              name: avalancheSubArea.Name,
              code: avalancheSubArea.Code,
              active: avalancheSubArea.IsActive,
            },
          });
          toast.success('Avalanche Sub Area is created successfully.');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          resetForm({
            values: {
              validateOnMount: true,
              name: avalancheSubAreaDto.Name,
              description: avalancheSubAreaDto.Description,
              active: avalancheSubAreaDto.IsActive,
            },
          });
          if (errors.entity) setEntityErrors({ ['Avalanche Sub Area']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateAvalancheSubAreas = (setSubmitting, resetForm, setErrors, avalancheSubAreaDto) => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}avalanchesubarea/${avalancheSubAreaDto.value}`;
    PutJson(url, avalancheSubAreaDto)
      .then(async (response) => {
        if (response.ok) {
          const avalancheSubArea = await response.json();
          updateData({});
          resetForm({
            values: {
              validateOnMount: true,
              name: avalancheSubArea.Name,
              active: avalancheSubArea.IsActive,
            },
          });
          toast.success('Avalanche Sub Area is updated successfully.');
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          resetForm({
            values: {
              validateOnMount: true,
              name: avalancheSubAreaDto.Name,
              active: avalancheSubAreaDto.IsActive,
            },
          });
          setErrors(errors);
          if (errors.entity) setEntityErrors({ ['Avalanche Sub']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateData = (data) => {
    if (mode === MODE.SUB_EDIT) {
      const index = subAreas.findIndex((x) => x.value === avalancheSubAreaData.value);
      if (index < 0) return;
      subAreas[index] = { ...avalancheSubAreaData };
      setSubAreas([...subAreas]);
    } else {
      avalancheSubAreaData.value = data.value;
      setSubAreas([...subAreas, data]);
    }
  };

  const breadCrumbs = () => {
    setMode(FORM_MODE.EDIT);
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    avalancheSubAreaData.name = values.name;
    avalancheSubAreaData.isDisabled = !values.active;
    avalancheSubAreaData.code = values.code;
    avalancheSubAreaData.active = values.active;
    avalancheSubAreaData.parentId = avalancheAreaData.value;
    const avalancheSubAreaDto = {
      ...avalancheSubAreaData,
      Name: values.name,
      AreaId: avalancheAreaData.value,
      IsActive: values.active ?? true,
    };

    if (mode === MODE.SUB_EDIT) {
      updateAvalancheSubAreas(setSubmitting, resetForm, setErrors, avalancheSubAreaDto);
    } else {
      createAvalancheSubAreas(setSubmitting, resetForm, setErrors, avalancheSubAreaDto);
    }
  };

  const onClickCancel = () => {
    cancelOperation();
  };

  const onEditButtonClick = async () => {
    setMode(MODE.SUB_EDIT);
  };

  const cancelOperation = () => {
    setMode(FORM_MODE.EDIT);
  };

  let prefix = avalancheSubAreaData.code;
  if (mode === MODE.SUB_CREATE) {
    prefix = avalancheAreaData.code ? String(avalancheAreaData.code).slice(0, 3) : '';
  }

  return (
    <>
      <Formik
        enableReinitialize
        initialTouched={{
          name: true,
          code: true,
        }}
        initialValues={{
          name: avalancheSubAreaData.name ?? '',
          code: prefix,
          active: !avalancheSubAreaData.isDisabled ?? false,
        }}
        validationSchema={Yup.object({
          name: Yup.string()
            .nullable()
            .max(50, 'Maximum 50 characters are allowed')
            .required('Avalanche Sub Area Name is a required field'),
          code: Yup.string()
            .nullable()
            .min(5, '5 digits are required')
            .required('Avalanche Sub Area Code is a required field'),
        })}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            <span className="awp-text-btn" onClick={breadCrumbs}>
              Edit Avalanche Areas
            </span>
            <span className="mx-3">&gt;</span>
            <span>{title}</span>
            <Card border="light" className="page-card">
              <Card.Header>
                <Row className="mt-4"></Row>
                {crud.getHeader()}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormInput
                      label="Avalanche Sub Area Name"
                      instruction="(50 Characters max)"
                      readOnly={readOnly || mode === MODE.SUB_EDIT}
                      name="name"
                      id="name"
                    />
                  </Col>
                  <Col sm={6} className="mb-4">
                    {mode === MODE.SUB_CREATE ? (
                      <SubAreaCodeInput
                        label="Code"
                        name="code"
                        instruction="(5 digits code)"
                        id="code"
                        parentId={avalancheAreaData.code}
                      />
                    ) : (
                      <FormInput label="Code" readOnly={readOnly || mode === MODE.SUB_EDIT} name="code" id="code" />
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormSwitch
                      useMargin={true}
                      readOnly={readOnly}
                      id="active"
                      name="active"
                      label="Active"
                      value={!avalancheSubAreaData.isDisabled}
                    />
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer>
                {!readOnly ? (
                  <AwpCloseCancelButtons
                    props={props}
                    isPristine={!props.dirty || props.isSubmitting}
                    hasAccess={true}
                    onConfirmedCancel={() => {
                      onClickCancel();
                    }}
                  />
                ) : (
                  <Authorize requires={[Permissions.WriteAdminTools]}>
                    <div className="mr-3">
                      <Button variant="outline-primary" onClick={() => onClickCancel()}>
                        {!props.dirty ? 'Close' : 'Cancel'}
                      </Button>
                      <Button className="ml-2" variant="outline-primary" onClick={onEditButtonClick}>
                        Edit Avalanche Sub Area
                      </Button>
                    </div>
                  </Authorize>
                )}
              </Card.Footer>
            </Card>
          </Form>
        )}
      </Formik>
      <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
    </>
  );
};

export default AvalancheSubAreasEdit;
